import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState, createContext, useContext, Suspense } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { resolveVisibleSections } from "@/lib/sections";
import { LayoutDashboard, Store, FileSpreadsheet, Scale, LogOut, PlusCircle, History, Users as UsersIcon, UserCircle, Receipt, Package, PackageOpen, Flame, Building2, Landmark, CalendarCheck2, Upload, BookOpen, Bell, Boxes, TagsIcon, Menu, X, Trophy, HandCoins, Home, Globe2, CreditCard, ArrowLeftRight, Megaphone, BellRing, Monitor, HardDrive, ShoppingBag, Link2, Radio, GitCompare, BarChart3, ClipboardList, HelpCircle, Users2 as Users2Icon, KeyRound, ChevronDown, ChevronRight, ExternalLink, UserCog, MessageCircle, ChefHat, Search, AlertTriangle, Handshake, Wheat, Star, ShieldCheck, Wallet, Calculator, Clock, Sparkles, Printer, Mail } from "lucide-react";
import AnnouncementViewer from "@/components/AnnouncementViewer";
import CommandPalette from "@/components/app/CommandPalette";
import useGlobalTrendyolLive from "@/hooks/useGlobalTrendyolLive";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Sidebar arama context — NavItem ve SectionHeader label'a göre filtrelesin diye
const SidebarSearchContext = createContext("");

// Favoriler context — kullanıcı yıldızladığı öğeleri sidebar üstünde görür
const FavoritesContext = createContext({ favs: [], toggleFav: () => {}, isFav: () => false });

// Türkçe-uyumlu diakritik-duyarsız match — ör. "sube" → "Şube"
function matchTr(haystack, needle) {
  const norm = (s) => (s || "")
    .toLocaleLowerCase("tr")
    .replace(/ı/g, "i").replace(/ğ/g, "g").replace(/ü/g, "u")
    .replace(/ş/g, "s").replace(/ö/g, "o").replace(/ç/g, "c");
  return norm(haystack).includes(norm(needle));
}

function NavItem({ to, icon: Icon, label, end, badge, sectionKey, visibleSet, accentColor, hideFav }) {
  const searchQuery = useContext(SidebarSearchContext);
  const { toggleFav, isFav } = useContext(FavoritesContext);
  // sectionKey verildiyse ve set içinde yoksa render etme
  if (sectionKey && visibleSet && !visibleSet.has(sectionKey)) return null;
  // Arama filtresi — label eşleşmiyorsa gizle
  if (searchQuery && !matchTr(label, searchQuery)) return null;
  const activeStyle = accentColor
    ? { backgroundColor: `${accentColor}14`, color: accentColor, borderLeftColor: accentColor }
    : undefined;
  const favored = isFav(to);
  return (
    <NavLink
      to={to}
      end={end}
      style={({ isActive }) => (isActive && accentColor ? activeStyle : undefined)}
      className={({ isActive }) =>
        `group flex items-center gap-2.5 pl-3 pr-2 py-2 rounded-md text-[13px] leading-tight transition-colors border-l-2 ${
          isActive
            ? (accentColor ? "font-medium" : "bg-[#F5EDE4] text-[#8B3A1A] font-medium border-[#D95D39]")
            : "text-slate-500 hover:bg-[#F6F4EF] hover:text-slate-800 border-transparent"
        }`
      }
      data-testid={`nav-${to.replace(/\//g, "-")}`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 opacity-80 group-hover:opacity-100" />
      <span className="flex-1 truncate">{label}</span>
      {badge > 0 && (
        <span
          className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#F0E3D7] text-[#8B3A1A] text-[10px] font-semibold tabular-nums"
          data-testid={`nav-badge-${to.replace(/\//g, "-")}`}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
      {!hideFav && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav(to, label); }}
          className={`shrink-0 p-0.5 rounded transition ${favored ? "opacity-100" : "opacity-0 group-hover:opacity-100"} hover:bg-slate-200/70`}
          title={favored ? "Favorilerden çıkar" : "Favorilere ekle"}
          data-testid={`fav-toggle-${to.replace(/\//g, "-")}`}
        >
          <Star className={`h-3 w-3 ${favored ? "fill-yellow-400 text-yellow-500" : "text-slate-400"}`} />
        </button>
      )}
    </NavLink>
  );
}

/** Sidebar bölüm başlığı — tıklandığında altındaki öğeleri açar/kapatır. */
function SectionHeader({ label, color = "#94a3b8", bg = "transparent", expanded = true, onToggle }) {
  const searchQuery = useContext(SidebarSearchContext);
  // Arama aktifken bölüm başlıklarını gizle (düz liste görünümü)
  if (searchQuery) return null;
  const Chev = expanded ? ChevronDown : ChevronRight;
  return (
    <div className="pt-3 mt-3 border-t border-[#ECE7DF]">
      <button
        type="button"
        onClick={onToggle}
        className="w-full mx-1 mb-1 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-[0.18em] font-display flex items-center justify-between hover:opacity-80 transition"
        style={{ color, backgroundColor: bg }}
        data-testid={`section-toggle-${label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <span>{label}</span>
        <Chev className="h-3 w-3 opacity-70" />
      </button>
    </div>
  );
}

// Lazy sayfa chunk'ı yüklenirken gösterilen hafif yükleniyor göstergesi (sidebar görünür kalır)
function PageLoader() {
  return (
    <div className="flex items-center justify-center py-24" data-testid="page-loader">
      <div className="h-7 w-7 rounded-full border-2 border-[#D95D39] border-t-transparent animate-spin" />
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Global Trendyol Canlı Sipariş dinleyicisi — hangi sayfada olursak olalım
  // yeni sipariş geldiğinde ses, bildirim, otomatik onay ve yazdırma çalışır.
  useGlobalTrendyolLive(user);
  const isAdmin = user?.role === "admin";
  const isBranchUser = user?.role === "branch";
  const isAccounting = user?.role === "accounting";
  const isRegional = user?.role === "regional_manager";
  const isOperations = user?.role === "operasyon";
  const isManager = isAdmin || isRegional || isAccounting || isOperations;
  // Bu kullanıcının görebileceği bölümlerin merkezi set'i.
  // Admin bunu Yöneticiler ekranından tek tek işaretleyerek düzenleyebilir.
  // Eğer allowed_sections tanımsızsa role'e göre varsayılan uygulanır.
  // Favoriler — kullanıcı yıldızladığı sidebar öğeleri (localStorage'da)
  const [favs, setFavs] = useState(() => {
    try {
      const raw = localStorage.getItem("sidebar_favorites");
      if (raw) { const arr = JSON.parse(raw); if (Array.isArray(arr)) return arr; }
    } catch { /* ignore */ }
    return [];
  });
  const isFavFn = (to) => favs.some((f) => f.to === to);
  const toggleFav = (to, label) => {
    setFavs((prev) => {
      const exists = prev.some((f) => f.to === to);
      const next = exists ? prev.filter((f) => f.to !== to) : [...prev, { to, label }].slice(0, 20);
      try { localStorage.setItem("sidebar_favorites", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const favoritesCtx = useMemo(() => ({ favs, toggleFav, isFav: isFavFn }), [favs]);

  const visibleSet = useMemo(() => resolveVisibleSections(user), [user]);
  // Unlav Lavaş Siparişi: kullanıcının erişebildiği en az bir aktif Unlav şubesi varsa göster
  const [hasUnlavAccess, setHasUnlavAccess] = useState(false);
  useEffect(() => {
    if (!user) return;
    api.get("/unlav/branches").then((r) => {
      setHasUnlavAccess((r.data?.branches || []).length > 0);
    }).catch(() => setHasUnlavAccess(false));
  }, [user?.id, user?.username]);
  // Merkez bölümü: şube/yönetici/merkez rapor/menü board/online panel menüleri
  const canSeeMerkez = useMemo(() => (
    [
      "branches", "users", "merkez-report", "merkez-expense-categories",
      "joker-takip", "menuboard", "online-menus", "recipes", "franchise", "unlav-orders",
      "desen-orders", "desen-match", "desen-cari-ekstre", "desen-category-windows", "desen-branch-windows",
    ].some((k) => visibleSet.has(k))
  ), [visibleSet]);
  // Operasyon: depolama, bildirim, duyuru, destek atamaları, denetim, online panel verileri
  const canSeeOperasyon = useMemo(() => (
    [
      "storage-usage", "notification-settings", "announcements",
      "support-settings", "audit-log", "online-panel",
    ].some((k) => visibleSet.has(k))
  ), [visibleSet]);
  // Finans: merkez personeli/günlük/hesaptan ödenenler/kira/yemek kartı/yemeksepeti mutabakat
  const canSeeFinans = useMemo(() => (
    [
      "head-office", "merkez-daily", "merkez-account-payments",
      "rent-tracking", "meal-card-payments", "ys-reconcile", "utility-bills", "migros-mutabakat",
    ].some((k) => visibleSet.has(k))
  ), [visibleSet]);
  // Trendyol grubu
  const canSeeTrendyol = useMemo(() => (
    [
      "trendyol", "trendyol-store-map", "trendyol-live",
      "trendyol-reconcile", "trendyol-analytics", "trendyol-products", "trendyol-mutabakat",
      "trendyol-anomalies",
    ].some((k) => visibleSet.has(k))
  ), [visibleSet]);
  // Platform Analitiği grubu (Trendyol/Yemek Sepeti/Migros analitik)
  const canSeePlatform = useMemo(() => (
    ["platform-analytics", "platform-uploads", "platform-matching"].some((k) => visibleSet.has(k))
  ), [visibleSet]);
  // Veri Girişleri grubu — herhangi biri açıksa header görün
  const canSeeDataEntry = useMemo(() => (
    ["expense-categories", "expense-analytics", "product-types", "stock-catalog", "report-new", "import"]
      .some((k) => visibleSet.has(k))
  ), [visibleSet]);
  // Raporlar grubu
  const canSeeReports = useMemo(() => (
    ["balance", "daily-summary", "stock-count", "branch-performance", "consumption", "reports"]
      .some((k) => visibleSet.has(k))
  ), [visibleSet]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [sidebarSearch, setSidebarSearch] = useState("");
  // Sidebar grup aç/kapa durumu (localStorage'a persiste edilir)
  // Default: yaygın gruplar (merkez, veri, raporlar) açık; ileri düzey (operasyon/finans/trendyol/platform) kapalı — göz karmaşasını azaltır
  const DEFAULT_GROUPS = { sube_islemleri: true, merkez: true, veri: true, raporlar: true, operasyon: false, finans: false, desen: false, trendyol: false, platform: false };
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const raw = localStorage.getItem("sidebar_groups");
      if (raw) return { ...DEFAULT_GROUPS, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return DEFAULT_GROUPS;
  });
  const toggleGroup = (key) => {
    setExpandedGroups((prev) => {
      // Accordion davranışı: aynı anda sadece TEK grup açık. Yeni bir grubu açarken diğerleri kapanır.
      const isOpen = !!prev[key];
      const allKeys = Object.keys(DEFAULT_GROUPS);
      const next = allKeys.reduce((acc, k) => ({ ...acc, [k]: false }), {});
      if (!isOpen) next[key] = true;  // kapalıysa aç; açıksa hepsi kapalı kalır
      try { localStorage.setItem("sidebar_groups", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  // Arama aktifken tüm gruplar açık gösterilsin (kapalı gruptaki eşleşmeler de görünsün)
  const groupsForRender = useMemo(() => (
    sidebarSearch.trim()
      ? { sube_islemleri: true, merkez: true, veri: true, raporlar: true, operasyon: true, finans: true, desen: true, trendyol: true, platform: true }
      : expandedGroups
  ), [sidebarSearch, expandedGroups]);
  const [pendingAdvances, setPendingAdvances] = useState(0);
  const [pendingPersonnel, setPendingPersonnel] = useState(0);
  const [pendingRents, setPendingRents] = useState({ count: 0, overdue: 0 });

  // Bekleyen avans talebi sayısı — sadece yönetici (admin/regional_manager) için
  useEffect(() => {
    if (!isAdmin && !isRegional) { setPendingAdvances(0); return; }
    let mounted = true;
    const fetchPending = async () => {
      try {
        const r = await api.get("/advance-requests", { params: { status: "pending" } });
        if (mounted) setPendingAdvances(Array.isArray(r.data) ? r.data.length : 0);
      } catch { /* sessiz */ }
    };
    fetchPending();
    const id = setInterval(fetchPending, 120000); // 2 dk'da bir tazele (pod yükünü azalt)
    return () => { mounted = false; clearInterval(id); };
  }, [isAdmin, isRegional]);

  // Bekleyen personel talebi sayısı — sadece yönetici
  useEffect(() => {
    if (!isAdmin && !isRegional) { setPendingPersonnel(0); return; }
    let mounted = true;
    const fetchPending = async () => {
      try {
        const r = await api.get("/personnel-requests", { params: { status: "pending" } });
        if (mounted) setPendingPersonnel(Array.isArray(r.data) ? r.data.length : 0);
      } catch { /* sessiz */ }
    };
    fetchPending();
    const id = setInterval(fetchPending, 120000);
    return () => { mounted = false; clearInterval(id); };
  }, [isAdmin, isRegional]);

  // Ödenmemiş kira sözleşme sayısı — admin/muhasebe
  useEffect(() => {
    if (!isAdmin && !isAccounting) { setPendingRents({ count: 0, overdue: 0 }); return; }
    let mounted = true;
    const fetchRent = async () => {
      try {
        const r = await api.get("/rent-contracts/pending-count");
        if (mounted) setPendingRents({ count: r.data.count || 0, overdue: r.data.overdue || 0 });
      } catch { /* sessiz */ }
    };
    fetchRent();
    const id = setInterval(fetchRent, 300000); // 5 dakikada bir tazele
    return () => { mounted = false; clearInterval(id); };
  }, [isAdmin, isAccounting]);

  // Rota değişince mobil menüyü kapat
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Dış Şube (Kasa programını kullanmayan): Unlav ve/veya DesenERP modüllerine erişebilir.
  // Eski `is_external_unlav` bayrağı `is_external` ile aynıdır (auth.py'de eşleştirildi).
  const isExternalBranch = !!(user?.is_external || user?.is_external_unlav);
  const extHasUnlav = !!user?.unlav_enabled;
  const extHasDesen = false;  // DesenERP entegrasyonu kaldırıldı
  useEffect(() => {
    if (!isExternalBranch) return;
    const p = location.pathname;
    const onUnlav = p.startsWith("/unlav-orders");
    const onDesen = p.startsWith("/desen-");
    if (onUnlav || onDesen) return;
    // İzin verilen ilk sayfaya yönlendir — DesenERP öncelikli (daha kapsamlı)
    if (extHasDesen) navigate("/desen-orders", { replace: true });
    else navigate("/unlav-orders", { replace: true });
  }, [isExternalBranch, extHasUnlav, extHasDesen, location.pathname, navigate]);

  // Dış Şube: minimal UI — üstte modül sekmeleri (Unlav / DesenERP) + çıkış
  if (isExternalBranch) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex flex-col">
        <div className="h-14 bg-white border-b border-[#EAE6DF] flex items-center justify-between px-4 sticky top-0 z-40 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-8 w-8 rounded-md bg-[#D95D39] flex items-center justify-center text-white font-bold font-display text-sm shrink-0">K</div>
            <div className="min-w-0">
              <div className="font-display font-semibold text-slate-800 leading-none text-[15px] truncate">{user?.name || user?.username}</div>
              <div className="text-[10px] text-slate-400 mt-0.5 tracking-wide uppercase">Dış Şube</div>
            </div>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto">
            {extHasUnlav && (
              <NavLink
                to="/unlav-orders"
                className={({ isActive }) =>
                  `text-xs px-3 py-1.5 rounded-md border transition whitespace-nowrap ${
                    isActive ? "bg-orange-500 border-orange-500 text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                  }`
                }
                data-testid="ext-nav-unlav"
              >Lavaş Siparişi</NavLink>
            )}
            {extHasDesen && (
              <>
                <NavLink
                  to="/desen-orders"
                  className={({ isActive }) =>
                    `text-xs px-3 py-1.5 rounded-md border transition whitespace-nowrap ${
                      isActive ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                    }`
                  }
                  data-testid="ext-nav-desen-orders"
                >DesenERP Sipariş</NavLink>
                <NavLink
                  to="/desen-cari-ekstre"
                  className={({ isActive }) =>
                    `text-xs px-3 py-1.5 rounded-md border transition whitespace-nowrap ${
                      isActive ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                    }`
                  }
                  data-testid="ext-nav-desen-ekstre"
                >Cari Ekstre</NavLink>
              </>
            )}
            <button
              type="button"
              onClick={() => { logout(); navigate("/login", { replace: true }); }}
              className="text-xs text-slate-600 hover:text-slate-900 border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50"
              data-testid="unlav-only-logout"
            >Çıkış yap</button>
          </div>
        </div>
        <main className="flex-1">
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex">
      {/* Mobil üst bar */}
      <div className="md:hidden fixed top-0 inset-x-0 h-14 bg-white border-b border-[#EAE6DF] z-40 flex items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-[#D95D39] flex items-center justify-center text-white font-bold font-display text-sm">K</div>
          <div className="font-display font-semibold text-slate-900 text-sm">Kasa</div>
        </Link>
        <button
          type="button"
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          className="p-2 rounded-lg hover:bg-[#F3F1EC] text-slate-700"
          aria-label="Menü"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobil overlay */}
      {mobileOpen && (
        <button
          type="button"
          aria-label="Menüyü kapat"
          className="md:hidden fixed inset-0 bg-slate-900/40 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`w-60 border-r border-[#ECE7DF] bg-[#FBFAF6] flex flex-col fixed inset-y-0 left-0 z-50 transition-transform duration-200 ease-out ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}>
        <div className="px-4 py-4 border-b border-[#ECE7DF]">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-[#D95D39] flex items-center justify-center text-white font-bold font-display text-sm">
              K
            </div>
            <div>
              <div className="font-display font-semibold text-slate-800 leading-none text-[15px]">Kasa</div>
              <div className="text-[10px] text-slate-400 mt-0.5 tracking-wide uppercase">Yönetim</div>
            </div>
          </Link>
        </div>

        {/* Sidebar arama — menü öğelerini filtrele */}
        <div className="px-3 py-2 border-b border-[#ECE7DF] bg-[#FBFAF6]">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={sidebarSearch}
              onChange={(e) => setSidebarSearch(e.target.value)}
              placeholder="Menüde ara..."
              className="w-full h-8 pl-7 pr-7 rounded-md border border-[#ECE7DF] bg-white text-[12px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-[#D95D39] focus:ring-1 focus:ring-[#D95D39]/30 transition"
              data-testid="sidebar-search-input"
            />
            {sidebarSearch && (
              <button
                type="button"
                onClick={() => setSidebarSearch("")}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700"
                title="Temizle"
                data-testid="sidebar-search-clear"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          <div className="mt-1 px-0.5 flex items-center justify-between text-[10px] text-slate-400">
            <span>Hızlı git</span>
            <kbd className="font-mono border border-[#ECE7DF] rounded px-1 bg-white text-slate-500" title="Cmd/Ctrl + K">⌘K</kbd>
          </div>
        </div>

        <SidebarSearchContext.Provider value={sidebarSearch.trim()}>
        <FavoritesContext.Provider value={favoritesCtx}>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {favs.length > 0 && !sidebarSearch.trim() && (
            <div className="mb-2 pb-2 border-b border-[#EAE6DF]">
              <div className="px-2 mb-1 text-[10px] font-bold uppercase tracking-wider text-yellow-700 flex items-center gap-1">
                <Star className="h-3 w-3 fill-yellow-400 text-yellow-500" /> Favoriler
              </div>
              <div className="space-y-0.5">
                {favs.map((f) => (
                  <NavLink
                    key={f.to}
                    to={f.to}
                    className={({ isActive }) => `group flex items-center gap-2 px-3 py-1.5 rounded-md text-[12.5px] transition-colors ${isActive ? "bg-yellow-50 text-yellow-900 font-medium" : "text-slate-600 hover:bg-[#F6F4EF] hover:text-slate-800"}`}
                    data-testid={`fav-nav-${f.to.replace(/\//g, "-")}`}
                  >
                    <Star className="h-3 w-3 fill-yellow-400 text-yellow-500 shrink-0" />
                    <span className="flex-1 truncate">{f.label}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav(f.to, f.label); }}
                      className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-slate-200/70"
                      title="Favorilerden çıkar"
                    >
                      <X className="h-3 w-3 text-slate-400" />
                    </button>
                  </NavLink>
                ))}
              </div>
            </div>
          )}
          {isAccounting && !user?.allowed_sections && (
            <>
              <NavItem to="/merkez-daily" icon={Landmark} label="Merkez Günlük" />
              <NavItem to="/meal-card-payments" icon={CreditCard} label="Yemek Kartı Ödemeleri" />
            </>
          )}
          {(isManager && !(isAccounting && !user?.allowed_sections)) && (
            <>
              {/* ── Ana Yönetim (default slate) ── */}
              <NavItem to="/" icon={LayoutDashboard} label="Panel" end sectionKey="panel" visibleSet={visibleSet} />

              {/* ── Şube İşlemleri (kırmızı) ── */}
              <SectionHeader label="Şube İşlemleri" color="#B91C1C" bg="#FEF2F2" expanded={expandedGroups.sube_islemleri} onToggle={() => toggleGroup("sube_islemleri")} />
              {groupsForRender.sube_islemleri && (<>
                <NavItem to="/branches" icon={Store} label="Şubeler" sectionKey="branches" visibleSet={visibleSet} accentColor="#B91C1C" />
                <NavItem to="/personnel" icon={UserCircle} label="Personel" sectionKey="personnel" visibleSet={visibleSet} accentColor="#B91C1C" />
                <NavItem to="/timesheet" icon={CalendarCheck2} label="Puantaj" sectionKey="timesheet" visibleSet={visibleSet} accentColor="#B91C1C" />
                <NavItem to="/zarf-yazdir" icon={Printer} label="Zarf Yazdır" sectionKey="timesheet" visibleSet={visibleSet} accentColor="#B91C1C" />
                <NavItem to="/advance-requests" icon={HandCoins} label="Avans Talepleri" badge={pendingAdvances} sectionKey="advance-requests" visibleSet={visibleSet} accentColor="#B91C1C" />
                <NavItem to="/personnel-requests" icon={UsersIcon} label="Personel Talepleri" badge={pendingPersonnel} sectionKey="personnel-requests" visibleSet={visibleSet} accentColor="#B91C1C" />
              </>)}

              {/* ── Veri Girişleri (teal) ── */}
              {canSeeDataEntry && (
                <>
                  <SectionHeader label="Veri Girişleri" color="#0F766E" bg="#ECFDF5" expanded={expandedGroups.veri} onToggle={() => toggleGroup("veri")} />
                  {groupsForRender.veri && (<>
                  <NavItem to="/expense-categories" icon={Receipt} label="Masraf Kalemleri" sectionKey="expense-categories" visibleSet={visibleSet} accentColor="#0F766E" />
                  <NavItem to="/expense-analytics" icon={BarChart3} label="Masraf Analizi" sectionKey="expense-analytics" visibleSet={visibleSet} accentColor="#0F766E" />
                  <NavItem to="/product-types" icon={Package} label="Ürün Listesi" sectionKey="product-types" visibleSet={visibleSet} accentColor="#0F766E" />
                  <NavItem to="/stock-catalog" icon={TagsIcon} label="Stok Kataloğu" sectionKey="stock-catalog" visibleSet={visibleSet} accentColor="#0F766E" />
                  <NavItem to="/report/new" icon={PlusCircle} label="Rapor Ekle" sectionKey="report-new" visibleSet={visibleSet} accentColor="#0F766E" />
                  <NavItem to="/import" icon={Upload} label="Toplu İçe Aktar" sectionKey="import" visibleSet={visibleSet} accentColor="#0F766E" />
                  </>)}
                </>
              )}

              {/* ── Raporlar (amber) ── */}
              {canSeeReports && (
                <>
                  <SectionHeader label="Raporlar" color="#B45309" bg="#FEF3C7" expanded={expandedGroups.raporlar} onToggle={() => toggleGroup("raporlar")} />
                  {groupsForRender.raporlar && (<>
                  <NavItem to="/balance" icon={Scale} label="Bilanço" sectionKey="balance" visibleSet={visibleSet} accentColor="#B45309" />
                  <NavItem to="/ai-assistant" icon={Sparkles} label="AI Asistan" accentColor="#7C3AED" />
                  <NavItem to="/daily-summary" icon={CalendarCheck2} label="Günlük Özetler" sectionKey="daily-summary" visibleSet={visibleSet} accentColor="#B45309" />
                  <NavItem to="/stock-count" icon={Boxes} label="Stok Sayımı" sectionKey="stock-count" visibleSet={visibleSet} accentColor="#B45309" />
                  <NavItem to="/branch-performance" icon={Trophy} label="Şube Performansı" sectionKey="branch-performance" visibleSet={visibleSet} accentColor="#B45309" />
                  <NavItem to="/consumption" icon={Flame} label="Fire / Zayi" sectionKey="consumption" visibleSet={visibleSet} accentColor="#B45309" />
                  <NavItem to="/reports" icon={FileSpreadsheet} label="Son Girilen Raporlar" sectionKey="reports" visibleSet={visibleSet} accentColor="#B45309" />
                  </>)}
                </>
              )}
              {canSeeMerkez && (
                <>
                  {/* ── Merkez (mor) ── */}
                  <SectionHeader label="Merkez" color="#6D28D9" bg="#F5F3FF" expanded={expandedGroups.merkez} onToggle={() => toggleGroup("merkez")} />
                  {groupsForRender.merkez && (<>
                    <NavItem to="/users" icon={UsersIcon} label="Yöneticiler" sectionKey="users" visibleSet={visibleSet} accentColor="#6D28D9" />
                    <NavItem to="/user-permissions" icon={ShieldCheck} label="Yetki Matrisi" sectionKey="user-permissions" visibleSet={visibleSet} accentColor="#6D28D9" />
                    <NavItem to="/merkez-report" icon={FileSpreadsheet} label="Merkez Rapor" sectionKey="merkez-report" visibleSet={visibleSet} accentColor="#6D28D9" />
                    <NavItem to="/merkez-expense-categories" icon={Receipt} label="Merkez Masraf Kalemleri" sectionKey="merkez-expense-categories" visibleSet={visibleSet} accentColor="#6D28D9" />
                    <NavItem to="/joker-takip" icon={UserCog} label="Joker Takip" sectionKey="joker-takip" visibleSet={visibleSet} accentColor="#6D28D9" />
                    <NavItem to="/franchise" icon={Handshake} label="Franchise Görüşmeleri" sectionKey="franchise" visibleSet={visibleSet} accentColor="#6D28D9" />
                    <NavItem to="/unlav-orders" icon={Wheat} label="Unlav Lavaş Siparişi" sectionKey="unlav-orders" visibleSet={visibleSet} accentColor="#6D28D9" />
                    <NavItem to="/menuboard" icon={Monitor} label="Menü Board" sectionKey="menuboard" visibleSet={visibleSet} accentColor="#6D28D9" />
                    <NavItem to="/online-menus" icon={ClipboardList} label="Menüler" sectionKey="online-menus" visibleSet={visibleSet} accentColor="#6D28D9" />
                    <NavItem to="/recipes" icon={ChefHat} label="Reçeteler" sectionKey="recipes" visibleSet={visibleSet} accentColor="#6D28D9" />
                  </>)}
                </>
              )}

              {canSeeOperasyon && (
                <>
                  {/* ── Operasyon (indigo) ── */}
                  <SectionHeader label="Operasyon" color="#4338CA" bg="#EEF2FF" expanded={expandedGroups.operasyon} onToggle={() => toggleGroup("operasyon")} />
                  {groupsForRender.operasyon && (<>
                    <NavItem to="/support" icon={HelpCircle} label="Talep / Destek" sectionKey="support" visibleSet={visibleSet} accentColor="#4338CA" />
                    <NavItem to="/whatsapp" icon={MessageCircle} label="WhatsApp" sectionKey="whatsapp" visibleSet={visibleSet} accentColor="#4338CA" />
                    <NavItem to="/storage-usage" icon={HardDrive} label="Depolama Kullanımı" sectionKey="storage-usage" visibleSet={visibleSet} accentColor="#4338CA" />
                    <NavItem to="/notification-settings" icon={BellRing} label="Bildirim Ayarları" sectionKey="notification-settings" visibleSet={visibleSet} accentColor="#4338CA" />
                    <NavItem to="/daily-email" icon={Mail} label="Günlük Kasa E-postası" sectionKey="daily-email" visibleSet={visibleSet} accentColor="#4338CA" />
                    <NavItem to="/announcements" icon={Megaphone} label="Duyurular" sectionKey="announcements" visibleSet={visibleSet} accentColor="#4338CA" />
                    <NavItem to="/support-settings" icon={Users2Icon} label="Destek Atamaları" sectionKey="support-settings" visibleSet={visibleSet} accentColor="#4338CA" />
                    <NavItem to="/audit-log" icon={History} label="Denetim Kaydı" sectionKey="audit-log" visibleSet={visibleSet} accentColor="#4338CA" />
                    <NavItem to="/online-panel" icon={Globe2} label="Online Panel Verileri" sectionKey="online-panel" visibleSet={visibleSet} accentColor="#4338CA" />
                  </>)}
                </>
              )}

              {canSeeFinans && (
                <>
                  {/* ── Finans (koyu yeşil) ── */}
                  <SectionHeader label="Finans" color="#166534" bg="#F0FDF4" expanded={expandedGroups.finans} onToggle={() => toggleGroup("finans")} />
                  {groupsForRender.finans && (<>
                    <NavItem to="/head-office" icon={Building2} label="Merkez Personeli" sectionKey="head-office" visibleSet={visibleSet} accentColor="#166534" />
                    <NavItem to="/muhasebe-cari" icon={Calculator} label="Muhasebe Cari" sectionKey="muhasebe-cari" visibleSet={visibleSet} accentColor="#166534" />
                    <NavItem to="/merkez-daily" icon={Landmark} label="Merkez Günlük" sectionKey="merkez-daily" visibleSet={visibleSet} accentColor="#166534" />
                    <NavItem to="/merkez-account-payments" icon={ArrowLeftRight} label="Hesaptan Ödenenler" sectionKey="merkez-account-payments" visibleSet={visibleSet} accentColor="#166534" />
                    <NavItem to="/rent-tracking" icon={Home} label="Kira Takibi" badge={pendingRents.count} sectionKey="rent-tracking" visibleSet={visibleSet} accentColor="#166534" />
                    <NavItem to="/utility-bills" icon={Receipt} label="Şube Fatura Takip" sectionKey="utility-bills" visibleSet={visibleSet} accentColor="#166534" />
                    <NavItem to="/migros/mutabakat" icon={Scale} label="Migros Mutabakat" sectionKey="migros-mutabakat" visibleSet={visibleSet} accentColor="#FF6B00" />
                    <NavItem to="/meal-card-payments" icon={CreditCard} label="Yemek Kartı Ödemeleri" sectionKey="meal-card-payments" visibleSet={visibleSet} accentColor="#166534" />
                    <NavItem to="/ys-reconcile" icon={FileSpreadsheet} label="Yemek Sepeti Mutabakat" sectionKey="ys-reconcile" visibleSet={visibleSet} accentColor="#166534" />
                  </>)}
                </>
              )}

              {(!isBranchUser || !!user?.desen_enabled) && (
                <>
                  {/* DesenERP kaldırıldı */}

                </>
              )}

              {canSeeTrendyol && (
                <>
                  {/* ── Trendyol Go (kırmızı) ── */}
                  <SectionHeader label="Trendyol Go" color="#DC2626" bg="#FEF2F2" expanded={expandedGroups.trendyol} onToggle={() => toggleGroup("trendyol")} />
                  {groupsForRender.trendyol && (<>
                    <NavItem to="/trendyol" end icon={ShoppingBag} label="Trendyol Ayarları" sectionKey="trendyol" visibleSet={visibleSet} accentColor="#DC2626" />
                    <NavItem to="/trendyol/store-map" icon={Link2} label="Şube Eşleştirme" sectionKey="trendyol-store-map" visibleSet={visibleSet} accentColor="#DC2626" />
                    <NavItem to="/trendyol/live" icon={Radio} label="Canlı Sipariş" sectionKey="trendyol-live" visibleSet={visibleSet} accentColor="#DC2626" />
                    <NavItem to="/trendyol/reconcile" icon={GitCompare} label="Şube Karşılaştırma" sectionKey="trendyol-reconcile" visibleSet={visibleSet} accentColor="#DC2626" />
                    <NavItem to="/trendyol/analytics" icon={BarChart3} label="Analitik" sectionKey="trendyol-analytics" visibleSet={visibleSet} accentColor="#DC2626" />
                    <NavItem to="/trendyol/products" icon={PackageOpen} label="Ürünler" sectionKey="trendyol-products" visibleSet={visibleSet} accentColor="#DC2626" />
                    <NavItem to="/trendyol/mutabakat" icon={Scale} label="Trendyol Mutabakat" sectionKey="trendyol-mutabakat" visibleSet={visibleSet} accentColor="#DC2626" />
                    <NavItem to="/trendyol/anomalies" icon={AlertTriangle} label="Anomaliler" sectionKey="trendyol-anomalies" visibleSet={visibleSet} accentColor="#DC2626" />
                    <NavItem to="/trendyol/claims" icon={ArrowLeftRight} label="İade Talepleri" sectionKey="trendyol-claims" visibleSet={visibleSet} accentColor="#DC2626" />
                  </>)}
                </>
              )}

              {/* ── Platform Analitiği (mor-mavi) ── */}
              {canSeePlatform && (
                <>
                  <SectionHeader label="Platform Analitiği" color="#7C3AED" bg="#F5F3FF" expanded={expandedGroups.platform} onToggle={() => toggleGroup("platform")} />
                  {groupsForRender.platform && (<>
                    <NavItem to="/platform" end icon={BarChart3} label="Genel Panel" sectionKey="platform-analytics" visibleSet={visibleSet} accentColor="#7C3AED" />
                    <NavItem to="/platform/uploads" icon={PlusCircle} label="Rapor Yükleme" sectionKey="platform-uploads" visibleSet={visibleSet} accentColor="#7C3AED" />
                    <NavItem to="/platform/matching" icon={Link2} label="Şube Eşleştirme" sectionKey="platform-matching" visibleSet={visibleSet} accentColor="#7C3AED" />
                  </>)}
                </>
              )}
            </>
          )}
          {!isManager && (
            <>
              <NavItem to="/" icon={LayoutDashboard} label="Panel" end />
              <NavItem to="/report/new" icon={PlusCircle} label="Günlük Rapor" />
              <NavItem to="/personnel" icon={UserCircle} label="Personelim" />
              <NavItem to="/timesheet" icon={CalendarCheck2} label="Puantaj" />
              <NavItem to="/advance-requests" icon={HandCoins} label="Avans Talepleri" />
              <NavItem to="/personnel-requests" icon={UsersIcon} label="Personel Talepleri" />
              <NavItem to="/stock-count" icon={Boxes} label="Stok Sayımı" />
              <NavItem to="/my-online-panel" icon={Globe2} label="Online Panel Bilgilerim" />
              <NavItem to="/support" icon={HelpCircle} label="Talep / Destek" />
              <NavItem to="/reports" icon={History} label="Geçmiş" />
              <NavItem to="/daily-summary" icon={CalendarCheck2} label="Günlük Özetler" />
              <NavItem to="/balance" icon={Scale} label="Aylık Bilanço" />
              {hasUnlavAccess && (
                <NavItem to="/unlav-orders" icon={Wheat} label="Unlav Lavaş Siparişi" />
              )}
              {/* Trendyol — visibleSet üzerinden kontrol edilir (admin izin verirse görünür) */}
              {(visibleSet.has("trendyol-live") || visibleSet.has("trendyol-analytics")) && (
                <div className="pt-3 mt-3 border-t border-[#ECE7DF]">
                  <div className="px-3 pt-1 pb-1.5 text-[9.5px] font-semibold uppercase tracking-[0.14em] text-slate-400">Trendyol Go</div>
                  <NavItem to="/trendyol/live" icon={Radio} label="Canlı Sipariş" sectionKey="trendyol-live" visibleSet={visibleSet} />
                  <NavItem to="/trendyol/analytics" icon={BarChart3} label="Trendyol Analitik" sectionKey="trendyol-analytics" visibleSet={visibleSet} />
                  <NavItem to="/trendyol/products" icon={PackageOpen} label="Ürünler" sectionKey="trendyol-products" visibleSet={visibleSet} />
                </div>
              )}
              {/* DesenERP (şube) kaldırıldı */}
            </>
          )}
          <div className="pt-3 mt-3 border-t border-[#ECE7DF]">
            <NavItem to="/help" icon={BookOpen} label="Kılavuz" />
          </div>
        </nav>
        </FavoritesContext.Provider>
        </SidebarSearchContext.Provider>

        <div className="p-3 border-t border-[#ECE7DF]">
          <div className="px-3 py-2 mb-2 rounded-md bg-[#F3EFE6]/70">
            <div className="text-[10px] text-slate-400 uppercase tracking-wide">Giriş yapan</div>
            <div className="text-[13px] font-medium text-slate-800 truncate">{user?.name || user?.username}</div>
            <div className="text-[10.5px] text-slate-500 mt-0.5">
              {isAdmin ? "Tam Yönetici" : isRegional ? "Bölge Müdürü" : isAccounting ? "Muhasebe" : isOperations ? "Operasyon" : "Şube"}
            </div>
          </div>
          <button
            data-testid="change-password-sidebar-btn"
            onClick={() => setPwOpen(true)}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-[12.5px] text-slate-500 hover:bg-[#F6F4EF] hover:text-slate-800 rounded-md transition-colors mb-1"
          >
            <KeyRound className="h-3.5 w-3.5" />
            Şifreyi Değiştir
          </button>
          <button
            data-testid="logout-btn"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-[12.5px] text-slate-500 hover:bg-red-50 hover:text-red-600 rounded-md transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
            Çıkış yap
          </button>
        </div>
      </aside>

      {/* Şifre Değiştir Modalı — herkese açık */}
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />

      {/* Main */}
      <main className="flex-1 md:ml-60 min-h-screen pt-14 md:pt-0 overflow-x-hidden">
        <NotificationBanner />
        <div className="max-w-[1400px] mx-auto p-4 md:p-6 lg:p-8">
          <AnnouncementViewer />
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </div>
      </main>
      <CommandPalette />
    </div>
  );
}

// Web Audio API ile programatik uyarı sesi çal — 3 kez artan tonda çınlama.
// Danger seviyesinde daha yüksek/daha uzun; harici mp3 gerektirmez.
function playAlertSound(severity = "warning") {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const isDanger = severity === "danger";
    const reps = isDanger ? 4 : 3;
    const baseFreq = isDanger ? 880 : 660;
    for (let i = 0; i < reps; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = baseFreq + i * 220;
      const t0 = now + i * 0.35;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.35, t0 + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.25);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.28);
    }
    // Bellek sızıntısı olmasın — bir süre sonra AudioContext'i kapat
    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 3000);
  } catch { /* ses çalınamıyorsa sessizce geç */ }
}

function NotificationBanner() {
  const { user } = useAuth();
  // Merkez rolleri için tüm dikkat çekici bildirimler (modal, tarayıcı push, ses, titreşim)
  // devre dışı — sadece üst banner. Şube kullanıcıları için varsayılan davranış korunur.
  const bannerOnlyMode = ["admin", "regional_manager", "accounting", "operasyon"].includes(user?.role || "");
  const [items, setItems] = useState([]);
  // Dismissal artık localStorage'da kalıcı olarak tutulur.
  // Bildirim id'sinde tarih olduğu için (örn. "rent-pending-2026-07-23") bir bildirimi
  // reddettiğinizde aynı gün tekrar açsanız da gösterilmez, ertesi gün yeni id ile
  // otomatik olarak yeniden görünür → "günde 1 defa" davranışı.
  const [dismissed, setDismissed] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("kasa_notif_dismissed") || "[]"));
    } catch { return new Set(); }
  });
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied",
  );
  // Sesi kullanıcı sekmeye tıklayana kadar tarayıcı bloklar; ilk etkileşim ile açılır.
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  useEffect(() => {
    const unlock = () => {
      setAudioUnlocked(true);
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
    window.addEventListener("click", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  const persistDismissed = (next) => {
    setDismissed(next);
    try {
      // 30 gün öncesinden eski id'leri temizle (id sonunda YYYY-MM-DD var).
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 30);
      const cutoffStr = cutoff.toISOString().slice(0, 10);
      const kept = [...next].filter((id) => {
        const m = String(id).match(/(\d{4}-\d{2}-\d{2})/);
        return !m || m[1] >= cutoffStr;
      });
      localStorage.setItem("kasa_notif_dismissed", JSON.stringify(kept));
    } catch { /* ignore */ }
  };

  // İlk yükleme + her 30 sn otomatik yenileme (daha erken tetiklensin)
  useEffect(() => {
    let ok = true;
    const seenNotifIds = new Set();
    const fetchNotifs = async () => {
      try {
        const r = await api.get("/notifications");
        if (!ok) return;
        const data = r.data || [];
        setItems(data);
        // Yeni bildirimler için tarayıcı popup + ses + titreşim
        data.forEach((n) => {
          if (!seenNotifIds.has(n.id) && !dismissed.has(n.id)) {
            seenNotifIds.add(n.id);
            // Merkez rolleri sessiz: ses/titreşim/tarayıcı bildirimi yok
            if (bannerOnlyMode) return;
            // Ses
            if (audioUnlocked) playAlertSound(n.severity);
            // Mobil titreşim
            try { if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 400]); } catch { /* ignore */ }
            // Tarayıcı native bildirim
            if (typeof Notification !== "undefined" && Notification.permission === "granted") {
              try {
                const nx = new Notification(n.title, {
                  body: n.body,
                  icon: "/favicon.ico",
                  tag: n.id,
                  requireInteraction: n.severity === "danger",
                });
                nx.onclick = () => { window.focus(); if (n.link) window.location.href = n.link; };
              } catch { /* ignore */ }
            }
          }
        });
      } catch { /* sessizce yut */ }
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 60_000);
    return () => { ok = false; clearInterval(interval); };
  }, [dismissed, audioUnlocked, bannerOnlyMode]);

  // Kritik/yüksek öncelikli bildirim için her 45 sn sesli hatırlatma (kullanıcı kapatana kadar)
  // Trendyol düşük puan uyarısı sesli tekrar tetiklemez — sadece pasif banner.
  // Merkez rolleri (admin/regional/accounting/operasyon) için sesli tekrar tamamen kapalı.
  useEffect(() => {
    if (!audioUnlocked || bannerOnlyMode) return undefined;
    const isAudible = (n) => n.type !== "trendyol_low_rating" && (n.severity === "danger" || n.severity === "warning");
    const criticals = items.filter((n) => !dismissed.has(n.id) && isAudible(n));
    if (!criticals.length) return undefined;
    const t = setInterval(() => {
      const still = items.filter((n) => !dismissed.has(n.id) && isAudible(n));
      if (still.length) {
        playAlertSound(still.some((n) => n.severity === "danger") ? "danger" : "warning");
        try { if (navigator.vibrate) navigator.vibrate([200, 100, 200]); } catch { /* ignore */ }
      }
    }, 45_000);
    return () => clearInterval(t);
  }, [items, dismissed, audioUnlocked, bannerOnlyMode]);

  const requestPermission = async () => {
    if (typeof Notification === "undefined") return;
    const p = await Notification.requestPermission();
    setPermission(p);
  };

  const visible = items.filter((n) => !dismissed.has(n.id));
  // Tam ekran modal SADECE gerçekten kritik (danger/warning) bildirimler için açılır.
  // "info" seviyesi (ör. Bekleyen Avans Talebi) sadece banner olarak gösterilir,
  // ekranı ele geçirip tıklamayı engellemesin.
  // Trendyol düşük puan uyarısı da modal olarak açılmaz — sadece üst banner'da gösterilir.
  // Merkez kullanıcıları (bannerOnlyMode) için modal HİÇBİR bildirimde açılmaz.
  const isBannerOnly = (n) => bannerOnlyMode || n.type === "trendyol_low_rating";
  const modalCandidates = visible.filter((n) => !isBannerOnly(n));
  const topCritical = modalCandidates.find((n) => n.severity === "danger")
    || modalCandidates.find((n) => n.severity === "warning")
    || null;
  const showPermPrompt = !bannerOnlyMode && visible.length > 0 && permission === "default";

  return (
    <>
      {/* Tam ekran modal — kritik bildirim ekranı ele geçirir, sesle birlikte gözle de yakalar */}
      {topCritical && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-in fade-in duration-200"
          data-testid="notification-modal"
          role="alertdialog"
          aria-labelledby="notif-modal-title"
        >
          <div
            className={`relative w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 ${
              topCritical.severity === "danger"
                ? "bg-gradient-to-br from-[#C84B31] via-[#D95D39] to-[#F27200]"
                : "bg-gradient-to-br from-[#F27200] via-[#F2A65A] to-[#FFC700]"
            }`}
          >
            {/* Pulsing ring */}
            <div className="absolute -top-6 -left-6 w-16 h-16 rounded-full bg-white/25 blur-md animate-pulse" aria-hidden="true" />
            <div className="absolute -bottom-6 -right-6 w-20 h-20 rounded-full bg-white/20 blur-md animate-pulse" aria-hidden="true" />

            <div className="relative p-8 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-14 w-14 rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center ring-4 ring-white/30 animate-pulse">
                  <Bell className="h-7 w-7 text-white" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-white/80">
                    {topCritical.severity === "danger" ? "Acil Hatırlatma" : "Hatırlatma"}
                  </div>
                  <div id="notif-modal-title" className="font-display text-2xl font-bold leading-tight">{topCritical.title}</div>
                </div>
              </div>
              <p className="text-white/95 text-base leading-relaxed mb-6">{topCritical.body}</p>
              <div className="flex flex-wrap gap-2">
                {topCritical.link && (
                  <a
                    href={topCritical.link}
                    className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-white text-slate-900 text-sm font-semibold hover:bg-white/90 transition"
                    data-testid={`notif-modal-open-${topCritical.id}`}
                  >
                    Şimdi Git →
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => { const next = new Set(dismissed); next.add(topCritical.id); persistDismissed(next); }}
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-white/15 backdrop-blur border border-white/30 text-white text-sm font-medium hover:bg-white/25 transition"
                  data-testid={`notif-modal-dismiss-${topCritical.id}`}
                >
                  Anladım, kapat
                </button>
              </div>
              <div className="mt-4 text-[11px] text-white/70">
                Bu hatırlatma her 45 saniyede bir sesli tekrar edilecek. Anlamak için &quot;Kapat&quot;a bas.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Küçük banner (ekstra bildirimler + izin promptu için) */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 pt-4 md:pt-6 space-y-2" data-testid="notification-banner">
        {showPermPrompt && (
          <div className="k-card p-3 flex items-center gap-3 border-l-4 border-[#4A6FA5] bg-[#EEF2F7]">
            <Bell className="h-4 w-4 text-[#4A6FA5]" />
            <div className="flex-1 text-xs text-slate-700">
              Tarayıcı bildirimlerine izin verirseniz sekme arka planda olsa bile hatırlatmaları görebilirsiniz.
            </div>
            <button onClick={requestPermission} className="text-xs font-medium text-[#4A6FA5] hover:underline whitespace-nowrap" data-testid="notif-permission-btn">
              İzin ver
            </button>
          </div>
        )}
        {visible.filter((n) => n.id !== topCritical?.id).map((n) => {
          const isDanger = n.severity === "danger";
          return (
            <div
              key={n.id}
              className={`k-card p-3 flex items-start gap-3 border-l-4 ${isDanger ? "border-[#C84B31] bg-[#FDF0ED]" : "border-[#F2A65A] bg-[#FFF6F1]"}`}
            >
              <Bell className={`h-4 w-4 mt-0.5 ${isDanger ? "text-[#C84B31]" : "text-[#B8451F]"}`} />
              <div className="flex-1">
                <div className={`font-semibold text-sm ${isDanger ? "text-[#8E2E1B]" : "text-slate-900"}`}>{n.title}</div>
                <div className="text-xs text-slate-600 mt-0.5">{n.body}</div>
              </div>
              {n.link && (
                <a href={n.link} className={`text-xs font-medium hover:underline whitespace-nowrap ${isDanger ? "text-[#C84B31]" : "text-[#D95D39]"}`} data-testid={`notif-link-${n.id}`}>
                  Görüntüle →
                </a>
              )}
              <button
                onClick={() => { const next = new Set(dismissed); next.add(n.id); persistDismissed(next); }}
                className="text-slate-400 hover:text-slate-600 text-lg leading-none px-1"
                aria-label="Kapat"
                data-testid={`notif-close-${n.id}`}
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}

function ChangePasswordDialog({ open, onOpenChange }) {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      setSubmitting(false);
    }
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!currentPw || !newPw) {
      toast.error("Lütfen tüm alanları doldurun");
      return;
    }
    if (newPw.length < 4) {
      toast.error("Yeni şifre en az 4 karakter olmalı");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("Yeni şifre ile tekrarı aynı olmalı");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      toast.success("Şifreniz başarıyla güncellendi");
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Şifre değiştirilemedi";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Şifreyi Değiştir</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cp-current">Mevcut Şifre</Label>
            <Input
              id="cp-current"
              type="password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              autoComplete="current-password"
              data-testid="cp-current-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-new">Yeni Şifre</Label>
            <Input
              id="cp-new"
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              autoComplete="new-password"
              data-testid="cp-new-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-confirm">Yeni Şifre (Tekrar)</Label>
            <Input
              id="cp-confirm"
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              autoComplete="new-password"
              data-testid="cp-confirm-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              data-testid="cp-cancel-btn"
            >
              İptal
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#D95D39] hover:bg-[#C84B31] text-white"
              data-testid="cp-submit-btn"
            >
              {submitting ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

